import type { ComputedRef } from 'vue';
import type {
  FetchOrganisationResponseLite,
  FetchUnitResponseLite,
  FetchAddressResponse,
  NestedOrganisationContactPerson,
} from 'src/api/models';
import type { EntitySearchConfig, DisplayProperty } from './ticket-entities/types';

type OrgSearchFn = (query: string, page: number) => Promise<{ data: FetchOrganisationResponseLite[]; page: number; total_pages: number }>;
type UnitSearchFn = (orgId: string, query: string) => Promise<{ data: FetchUnitResponseLite[]; page: number; total_pages: number }>;
type BranchSearchFn = (orgId: string | null, query: string, page: number) => Promise<{ data: FetchAddressResponse[]; page: number; total_pages: number }>;
type ContactSearchFn = (orgId: string, query: string, page: number) => Promise<{ data: NestedOrganisationContactPerson[]; page: number; total_pages: number }>;

export function createContractOrganisationConfig(searchFn: OrgSearchFn): EntitySearchConfig<FetchOrganisationResponseLite> {
  return {
    entityType: 'organisation',
    icon: 'pi-building',

    getDisplayName: (org) => org.name || 'Unknown Organisation',

    getDisplayProps: (org): DisplayProperty[] => [
      org.number != null ? { key: 'Number', value: `#${org.number}`, copyable: true } : null,
      org.type ? { key: 'Type', value: String(org.type) } : null,
      org.vat_number ? { key: 'VAT', value: org.vat_number, copyable: true } : null,
    ].filter((p) => p !== null) as DisplayProperty[],

    searchFields: [
      { name: 'name', weight: 2 },
      { name: 'number', weight: 2 },
      { name: 'vat_number', weight: 1 },
    ],

    async fetchAll({ page }) {
      const result = await searchFn('', page);
      return { data: result.data, hasMore: page < result.total_pages };
    },

    async apiSearch(query, { page }) {
      const result = await searchFn(query, page);
      return { data: result.data, hasMore: page < result.total_pages };
    },

    formatResult: (org) => ({
      id: org.id || '',
      title: [org.name, org.number != null && `#${org.number}`].filter(Boolean).join(' '),
      subtitle: org.type || '',
      originalItem: org,
    }),
  };
}

export function createContractUnitConfig(
  customerIdRef: ComputedRef<string | undefined>,
  searchFn: UnitSearchFn,
): EntitySearchConfig<FetchUnitResponseLite> {
  return {
    entityType: 'unit',
    icon: 'pi-truck',

    getDisplayName: (unit) => {
      if (unit.name) return unit.name;
      if (unit.fleet_number != null) return `Fleet #${unit.fleet_number}`;
      return 'Unknown Unit';
    },

    getDisplayProps: (unit): DisplayProperty[] => [
      unit.fleet_number != null ? { key: 'Fleet #', value: String(unit.fleet_number), copyable: true } : null,
      unit.serial_number ? { key: 'S/N', value: unit.serial_number, copyable: true } : null,
      unit.manufacture_name ? { key: 'Make', value: unit.manufacture_name } : null,
      unit.category?.name ? { key: 'Category', value: unit.category.name } : null,
    ].filter((p) => p !== null) as DisplayProperty[],

    searchFields: [
      { name: 'name', weight: 2 },
      { name: 'fleet_number', weight: 2 },
      { name: 'serial_number', weight: 2 },
      { name: 'manufacture_name', weight: 1 },
    ],

    async fetchAll() {
      const orgId = customerIdRef.value;
      if (!orgId) return { data: [], hasMore: false };
      const result = await searchFn(orgId, '');
      return { data: result.data, hasMore: false };
    },

    async apiSearch(query) {
      const orgId = customerIdRef.value;
      if (!orgId) return { data: [], hasMore: false };
      const result = await searchFn(orgId, query);
      return { data: result.data, hasMore: false };
    },

    formatResult: (unit) => {
      const parts: string[] = [];
      if (unit.fleet_number != null) parts.push(`#${unit.fleet_number}`);
      if (unit.manufacture_name) parts.push(unit.manufacture_name);
      return {
        id: unit.id || '',
        title: unit.name || parts.join(' - ') || 'Unknown Unit',
        subtitle: [unit.serial_number && `S/N: ${unit.serial_number}`, unit.category?.name].filter(Boolean).join(' - '),
        originalItem: unit,
      };
    },
  };
}

export function createContractBranchConfig(
  supplierIdRef: ComputedRef<string | undefined>,
  searchFn: BranchSearchFn,
): EntitySearchConfig<FetchAddressResponse> {
  return {
    entityType: 'site',
    icon: 'pi-map-marker',

    getDisplayName: (addr) => {
      const street = [addr.street, addr.house_number, addr.house_number_extension].filter(Boolean).join(' ');
      return street || addr.city || 'Unknown Location';
    },

    getDisplayProps: (addr): DisplayProperty[] => {
      const street = [addr.street, addr.house_number, addr.house_number_extension].filter(Boolean).join(' ');
      return [
        street ? { key: 'Address', value: street, copyable: true } : null,
        (addr.post_code || addr.city) ? { key: 'City', value: [addr.post_code, addr.city].filter(Boolean).join(' ') } : null,
        addr.country ? { key: 'Country', value: addr.country } : null,
      ].filter((p) => p !== null) as DisplayProperty[];
    },

    searchFields: [
      { name: 'street', weight: 2 },
      { name: 'city', weight: 2 },
      { name: 'post_code', weight: 1 },
    ],

    async fetchAll({ page }) {
      const orgId = supplierIdRef.value ?? null;
      const result = await searchFn(orgId, '', page);
      return { data: result.data, hasMore: page < result.total_pages };
    },

    async apiSearch(query, { page }) {
      const orgId = supplierIdRef.value ?? null;
      const result = await searchFn(orgId, query, page);
      return { data: result.data, hasMore: page < result.total_pages };
    },

    formatResult: (addr) => {
      const street = [addr.street, addr.house_number].filter(Boolean).join(' ');
      const city = [addr.post_code, addr.city].filter(Boolean).join(' ');
      return {
        id: addr.id || '',
        title: [street, city].filter(Boolean).join(', ') || 'Unknown Location',
        subtitle: addr.country || '',
        originalItem: addr,
      };
    },
  };
}

export function createContractContactConfig(
  customerIdRef: ComputedRef<string | undefined>,
  searchFn: ContactSearchFn,
): EntitySearchConfig<NestedOrganisationContactPerson> {
  return {
    entityType: 'contact_person',
    icon: 'pi-user',

    getDisplayName: (cp) => {
      const name = [cp.first_name, cp.last_name].filter(Boolean).join(' ');
      return name || cp.email || 'Unknown Contact';
    },

    getDisplayProps: (cp): DisplayProperty[] => [
      cp.email ? { key: 'Email', value: cp.email, copyable: true } : null,
      cp.phone_number ? { key: 'Phone', value: cp.phone_number, copyable: true } : null,
      cp.mobile_phone_number ? { key: 'Mobile', value: cp.mobile_phone_number, copyable: true } : null,
    ].filter((p) => p !== null) as DisplayProperty[],

    searchFields: [
      { name: 'first_name', weight: 2 },
      { name: 'last_name', weight: 2 },
      { name: 'email', weight: 1.5 },
      { name: 'phone_number', weight: 1 },
    ],

    async fetchAll({ page }) {
      const orgId = customerIdRef.value;
      if (!orgId) return { data: [], hasMore: false };
      const result = await searchFn(orgId, '', page);
      return { data: result.data, hasMore: page < result.total_pages };
    },

    async apiSearch(query, { page }) {
      const orgId = customerIdRef.value;
      if (!orgId) return { data: [], hasMore: false };
      const result = await searchFn(orgId, query, page);
      return { data: result.data, hasMore: page < result.total_pages };
    },

    formatResult: (cp) => {
      const name = [cp.first_name, cp.last_name].filter(Boolean).join(' ');
      return {
        id: cp.id || '',
        title: name || cp.email || 'Unknown Contact',
        subtitle: cp.email || '',
        originalItem: cp,
      };
    },
  };
}
